import axios from 'axios';
import { AdminUser, LoginRequest, LoginResponse } from '@redex/shared';

// Extended login request with reCAPTCHA
interface ExtendedLoginRequest extends LoginRequest {
  recaptchaToken?: string;
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api', // Use relative URL to go through Vite proxy
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('redex_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, remove it
      localStorage.removeItem('redex_admin_token');
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export class AuthService {
  /**
   * Login with email and password
   */
  async login(credentials: ExtendedLoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post('/auth/login', credentials);
      
      // Backend returns nested structure: { success: true, data: { token, user, ... } }
      // Transform it to match our LoginResponse interface
      if (response.data.success && response.data.data) {
        return {
          success: true,
          token: response.data.data.token,
          user: response.data.data.user,
          message: 'Login successful',
        };
      }
      
      throw new Error(response.data.message || 'Login failed');
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Login failed');
      }
      throw new Error('Network error during login');
    }
  }

  /**
   * Verify token and get user information
   */
  async verifyToken(token: string): Promise<AdminUser | null> {
    try {
      const response = await api.get('/auth/verify', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Backend returns nested structure: { success: true, data: { user: ... } }
      if (response.data.success && response.data.data?.user) {
        return response.data.data.user;
      }
      return null;
    } catch (error) {
      console.error('Token verification failed:', error);
      return null;
    }
  }

  /**
   * Logout and invalidate token
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem('redex_admin_token');
    }
  }

  /**
   * Get current user from token
   */
  async getCurrentUser(): Promise<AdminUser | null> {
    try {
      const response = await api.get('/auth/me');
      
      // Backend returns nested structure: { success: true, data: { user: ... } }
      if (response.data.success && response.data.data?.user) {
        return response.data.data.user;
      }
      return null;
    } catch (error) {
      console.error('Get current user failed:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('redex_admin_token');
    return !!token;
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('redex_admin_token');
  }

  /**
   * Set token in storage
   */
  setToken(token: string): void {
    localStorage.setItem('redex_admin_token', token);
  }

  /**
   * Remove token from storage
   */
  removeToken(): void {
    localStorage.removeItem('redex_admin_token');
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export configured axios instance for other services
export { api };
export default authService;
